import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, CircularProgress, Stack, Chip, IconButton, Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useSocketIO } from '../../providers/SocketIOProvider';
import type { UberNewOrderEvent } from '../../providers/SocketIOProvider';
import config from '../../config';
import { getValidToken } from '../../services/tokenRefreshService';
import UberOrderCard from './UberOrderCard';
import type { UberOrderData } from './UberOrderCard';
import { useOrderSound } from '../../hooks/useOrderSound';
import OrderSoundControls from '../../components/OrderSoundControls';
import SoundUnlockBanner from '../../components/SoundUnlockBanner';

/**
 * Safety-net refetch. The socket is the fast path; this catches any order it missed
 * (dropped connection, deploy, sleeping laptop) within half a minute.
 */
const POLL_MS = 30000;

export default function UberOrders() {
  const [orders, setOrders] = useState<UberOrderData[]>([]);
  const [loading, setLoading] = useState(true);
  const [newOrderIds, setNewOrderIds] = useState<Set<string>>(new Set());
  const { isConnected, onUberNewOrder } = useSocketIO();
  const { muted, playSound } = useOrderSound('uber');

  // Latest playSound without re-registering listeners/timers (it changes with mute).
  const playSoundRef = useRef(playSound);
  useEffect(() => {
    playSoundRef.current = playSound;
  }, [playSound]);

  // Every Uber order id this page has already shown. The first load seeds it silently;
  // after that, any id not in here — from the socket OR a refetch — is highlighted once.
  const knownIdsRef = useRef<Set<string>>(new Set());
  const seededRef = useRef(false);

  // `sound` is only for orders the refetch found: a socket order has already been
  // played app-wide by OrderNotificationProvider, but one the socket missed has not.
  const announce = useCallback((incoming: UberOrderData[], highlight: boolean, sound: boolean) => {
    const known = knownIdsRef.current;
    const fresh = incoming.filter((o) => o.uberOrderId && !known.has(o.uberOrderId));
    fresh.forEach((o) => known.add(o.uberOrderId));
    if (!highlight || fresh.length === 0) return;

    const ids = fresh.map((o) => o.id);
    setNewOrderIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });

    if (sound) playSoundRef.current();

    // Remove "new" highlight after 10 seconds
    setTimeout(() => {
      setNewOrderIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }, 10000);
  }, []);

  const fetchOrders = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const token = await getValidToken();
      const resp = await fetch(`${config.API_URL}/admin/uber-orders?limit=100&unresolved=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      const list: UberOrderData[] = json.data || [];
      setOrders(list);
      announce(list, seededRef.current, true);
      seededRef.current = true;
    } catch (err) {
      console.error('Failed to fetch Uber orders:', err);
    }
    if (showSpinner) setLoading(false);
  }, [announce]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Quiet refetch every POLL_MS, and straight after every (re)connect so a gap in the
  // socket is caught up immediately rather than on the next tick.
  useEffect(() => {
    const id = setInterval(() => fetchOrders(false), POLL_MS);
    return () => clearInterval(id);
  }, [fetchOrders]);

  useEffect(() => {
    if (isConnected) fetchOrders(false);
  }, [isConnected, fetchOrders]);

  // Auto-refresh at midnight to clear yesterday's orders from view
  useEffect(() => {
    const scheduleNextMidnight = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const msUntilMidnight = midnight.getTime() - now.getTime();
      return setTimeout(() => {
        fetchOrders();
        // Schedule the next midnight after refresh
        timerId = scheduleNextMidnight();
      }, msUntilMidnight);
    };
    let timerId = scheduleNextMidnight();
    return () => clearTimeout(timerId);
  }, [fetchOrders]);

  // The admin:uber-orders room is joined app-wide by OrderNotificationProvider,
  // which also plays the Uber Eats order sound on every page — this page only
  // adds new orders to its list.

  // Listen for new Uber order events
  useEffect(() => {
    const handleNewOrder = (order: UberNewOrderEvent) => {
      setOrders((prev) => {
        // Don't add duplicate (a scheduled order's "due" announcement refreshes its row instead)
        if (prev.some((o) => o.uberOrderId === order.uberOrderId)) {
          return prev.map((o) => (o.uberOrderId === order.uberOrderId ? { ...o, ...(order as UberOrderData) } : o));
        }
        return [order as UberOrderData, ...prev];
      });
      if ((order as { due?: boolean }).due) {
        // already known, so announce() would skip it: highlight it directly (the sound played app-wide)
        const id = (order as UberOrderData).id;
        setNewOrderIds((prev) => new Set(prev).add(id));
        setTimeout(() => setNewOrderIds((prev) => { const next = new Set(prev); next.delete(id); return next; }), 10000);
        return;
      }
      announce([order as UberOrderData], true, false);
    };

    const unsubscribe = onUberNewOrder(handleNewOrder);
    return unsubscribe;
  }, [onUberNewOrder, announce]);

  // Only show today's orders — stale orders from yesterday are hidden automatically. A scheduled
  // order placed on an earlier day stays until it is collected (or cancelled).
  const todaysOrders = orders.filter((o) => {
    const d = new Date(o.createdAt);
    const now = new Date();
    if (o.scheduled && o.status === 'new') return true;
    // cancelled after it was rung up: stays until the sale is voided or kept (Jason, 27 Oct 2026)
    if (o.status === 'cancelled' && o.vendSaleId && !o.vendVoidedAt && !o.saleKeptAt) return true;
    if (o.scheduledFor && new Date(o.scheduledFor).toDateString() === now.toDateString()) return true;
    return d.toDateString() === now.toDateString();
  });

  return (
    <Box p={3}>
      <SoundUnlockBanner muted={muted} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={700}>
            Uber Orders
          </Typography>
          <Chip label={`${todaysOrders.length} today`} size="small" color="primary" />
          <Chip label={`${orders.length} all time`} size="small" variant="outlined" />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <OrderSoundControls channel="uber" />
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchOrders()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && orders.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : todaysOrders.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}>
          <Typography color="text.secondary">No Uber orders today</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {todaysOrders.map((order) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={order.id || order.uberOrderId}>
              <UberOrderCard
                order={order}
                isNew={newOrderIds.has(order.id)}
                onChange={(patch) => setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...patch } : o)))}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
